import { detectEnvironment, getEnvironmentConfig } from './config/environment';
import { FunnelClient } from './funnelClient';
import { ApiClient, isRejectedSession } from './resources/apiClient';
import {
  AnonymousTokenResponse,
  ApiConfig,
  AuthState,
  Currency,
  Customer,
  DeepPartial,
  Environment,
  EnvironmentConfig,
  Locale,
  Session,
  SessionInitResponse,
  Store,
} from './types';
import { collectDeviceInfo, getBrowserLocale, getUrlParams } from './utils/deviceInfo';
import { EventBus } from './utils/eventBus';
import { EventDispatcher } from './utils/eventDispatcher';
import { decodeJWTClient, isTokenExpired } from './utils/jwtDecoder';
import { loadPluginConfig, PluginConfig, RawPluginConfig } from './utils/pluginConfig';
import { handlePreviewMode, isDraftMode, setDraftMode } from './utils/previewMode';
import { getClientToken, setClientToken } from './utils/tokenStorage';
import { shouldResolveAuthCode, resolveAuthHandoff } from './utils/authHandoff';



export interface TagadaClientConfig {
  environment?: Environment;
  customApiConfig?: DeepPartial<EnvironmentConfig>;
  debugMode?: boolean;
  localConfig?: string;
  rawPluginConfig?: RawPluginConfig;
  blockUntilSessionReady?: boolean;
  /**
   * Optional feature flags to enable/disable subsystems.
   * By default all features are enabled.
   */
  features?: {
    /**
     * Funnel session + navigation layer.
     * Set to false to completely disable funnel behaviour.
     * Pass object to configure behavior.
     */
    funnel?: boolean | {
      autoRedirect?: boolean;
      /** Skip automatic funnel initialization (use when caller handles init explicitly) */
      skipAutoInit?: boolean;
    };
  };
}

export interface TagadaState {
  auth: AuthState;
  session: Session | null;
  customer: Customer | null;
  locale: Locale;
  currency: Currency;
  store: Store | null;
  environment: EnvironmentConfig;
  isLoading: boolean;
  isInitialized: boolean;
  isSessionInitialized: boolean;
  pluginConfig: PluginConfig;
  pluginConfigLoading: boolean;
  debugMode: boolean;
  token: string | null;
}

export class TagadaClient {
  public apiClient: ApiClient;
  public state: TagadaState;
  /**
   * Optional funnel client.
   * Exposed so higher-level SDKs (React, Standalone, Vue) can reuse the same
   * low-level logic without instantiating a second client.
   */
  public funnel?: FunnelClient;
  /**
   * Event bus for domain events and coordination
   */
  public bus = new EventBus();

  private eventDispatcher = new EventDispatcher<TagadaState>();
  private tokenPromise: Promise<string | null> | null = null;
  private tokenResolver: ((token: string | null) => void) | null = null;
  // The token the server refused and this page replaced, and that replacement: one renewal per page, shared by the
  // calls the same refusal hit
  private renewedToken: string | null = null;
  private tokenRenewal: Promise<string | null> | null = null;
  private boundHandleStorageChange: () => void;
  private boundHandlePageshow: (event: PageTransitionEvent) => void;

  // Config
  private readonly config: TagadaClientConfig;
  private instanceId: string;

  // Track initialization state to prevent infinite loops
  private isInitializingSession = false;
  private lastSessionInitError: Error | null = null;
  private sessionInitRetryCount = 0;
  private readonly MAX_SESSION_INIT_RETRIES = 3;

  constructor(config: TagadaClientConfig = {}) {
    this.config = config;
    this.instanceId = Math.random().toString(36).substr(2, 9);
    this.boundHandleStorageChange = this.handleStorageChange.bind(this);
    this.boundHandlePageshow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        if (this.state.debugMode) {
          console.log(`[TagadaClient ${this.instanceId}] Page restored from BFcache (back button), re-initializing funnel...`);
        }

        // If we have an active session and store, we only need to re-initialize the funnel
        // This ensures tracking is correct and the session is fresh on the backend
        if (this.funnel && this.state.session && this.state.store) {
          this.funnel.resetInitialization();

          const accountId = this.getAccountId();
          const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
          const funnelId = urlParams.get('funnelId') || undefined;

          this.funnel.autoInitialize(
            { customerId: this.state.session.customerId, sessionId: this.state.session.sessionId },
            { id: this.state.store.id, accountId },
            funnelId,
          ).catch((err) => {
            console.error('[TagadaClient] Funnel re-initialization failed:', err);
          });
        } else {
          // If state is missing, perform a full initialization
          this.sessionInitRetryCount = 0;
          this.initialize();
        }
      }
    };

    console.log(`[TagadaClient ${this.instanceId}] Initializing...`);
    console.log(`[TagadaClient ${this.instanceId}] Config:`, {
      debugMode: config.debugMode,
      hasRawPluginConfig: !!config.rawPluginConfig,
      rawPluginConfig: config.rawPluginConfig,
      features: config.features,
    });

    // Handle preview mode FIRST - clears state if needed
    // This ensures clean state when CRM previews pages
    const previewModeActive = handlePreviewMode(this.config.debugMode);
    if (previewModeActive && this.config.debugMode) {
      console.log(`[TagadaClient ${this.instanceId}] Preview mode active - state cleared`);
    }

    // Initialize default state
    const env = this.resolveEnvironment();
    let envConfig = getEnvironmentConfig(env);

    // Apply custom API config if provided
    if (config.customApiConfig) {
      envConfig = {
        ...envConfig,
        ...config.customApiConfig,
        apiConfig: {
          ...envConfig.apiConfig,
          ...config.customApiConfig.apiConfig,
        } as ApiConfig,
      };
      if (this.config.debugMode) {
        console.log(`[TagadaClient ${this.instanceId}] Applied custom API config:`, envConfig.apiConfig.baseUrl);
      }
    }

    this.state = {
      auth: {
        isAuthenticated: false,
        isLoading: false,
        customer: null,
        session: null,
      },
      session: null,
      customer: null,
      locale: {
        locale: 'en-US',
        language: 'en',
        region: 'US',
        messages: {},
      },
      currency: {
        code: 'USD',
        symbol: '$',
        name: 'US Dollar',
      },
      store: null,
      environment: envConfig,
      isLoading: true,
      isInitialized: false,
      isSessionInitialized: false,
      pluginConfig: { basePath: '/', config: {} },
      pluginConfigLoading: true, // Always true - loadPluginConfig will process rawPluginConfig
      debugMode: config.debugMode ?? env !== 'production',
      token: null,
    };

    console.log(`[TagadaClient ${this.instanceId}] Initial state:`, {
      pluginConfigLoading: this.state.pluginConfigLoading,
      hasRawPluginConfig: !!config.rawPluginConfig,
    });

    // Initialize API Client
    this.apiClient = new ApiClient({
      baseURL: envConfig.apiConfig.baseUrl,
      onRejectedToken: this.renewRejectedToken.bind(this),
    });

    // Initialize optional funnel client (feature-flagged)
    const funnelFeature = config.features?.funnel;
    const funnelEnabled = funnelFeature !== false;

    if (funnelEnabled) {
      const funnelConfig = typeof funnelFeature === 'object' ? funnelFeature : {};

      this.funnel = new FunnelClient({
        apiClient: this.apiClient,
        debugMode: this.state.debugMode,
        pluginConfig: this.state.pluginConfig,
        environment: this.state.environment,
        autoRedirect: funnelConfig.autoRedirect,
        // Pass funnelId and stepId from rawPluginConfig to enable config-based initialization
        funnelId: config.rawPluginConfig?.funnelId,
        stepId: config.rawPluginConfig?.stepId,
      });
    }

    // Setup token waiting mechanism
    this.apiClient.setTokenProvider(this.waitForToken.bind(this));

    // Listen for storage changes (cross-tab sync)
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.boundHandleStorageChange);
      window.addEventListener('pageshow', this.boundHandlePageshow);
    }

    // Setup config hot-reload listener (for live config editing)
    this.setupConfigHotReload();

    // Start initialization
    this.initialize();
  }

  /**
   * Cleanup client resources
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.boundHandleStorageChange);
      window.removeEventListener('pageshow', this.boundHandlePageshow);
    }
    if (this.state.debugMode) {
      console.log(`[TagadaClient ${this.instanceId}] Destroyed`);
    }
    this.eventDispatcher.clear();
    this.bus.clear();
  }

  /**
   * Handle storage changes (e.g. token update in another tab)
   */
  private handleStorageChange() {
    const storedToken = getClientToken();

    // Avoid unnecessary re-initialization if token hasn't changed
    if (storedToken === this.state.token) {
      if (this.state.debugMode) {
        console.log(`[TagadaClient ${this.instanceId}] Token unchanged (ignoring event)`);
      }
      return;
    }

    // Prevent infinite loop: Don't re-initialize if we're currently initializing
    if (this.isInitializingSession) {
      if (this.state.debugMode) {
        console.log(`[TagadaClient ${this.instanceId}] Session initialization in progress, skipping storage change`);
      }
      return;
    }

    // Prevent infinite loop: Don't retry if we've hit max retries
    if (this.sessionInitRetryCount >= this.MAX_SESSION_INIT_RETRIES && this.lastSessionInitError) {
      if (this.state.debugMode) {
        console.error(`[TagadaClient ${this.instanceId}] Max session init retries reached, giving up`, this.lastSessionInitError);
      }
      return;
    }

    // Re-run initialization when token may have changed
    if (this.state.debugMode) {
      console.log(`[TagadaClient ${this.instanceId}] Storage changed, re-initializing token...`);
    }
    this.initializeToken();
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: (state: TagadaState) => void): () => void {
    return this.eventDispatcher.subscribe(listener);
  }

  /**
   * Get current state
   */
  getState(): TagadaState {
    return this.state;
  }

  /**
   * Update state and notify listeners
   */
  private updateState(updates: Partial<TagadaState>) {
    this.state = { ...this.state, ...updates };
    this.eventDispatcher.notify(this.state);
  }

  /**
   * Resolve environment
   */
  private resolveEnvironment(): Environment {
    if (this.config.environment) return this.config.environment;
    return detectEnvironment();
  }

  /**
   * Main initialization flow
   */
  private async initialize() {
    try {
      // 1. Load Plugin Config
      await this.initializePluginConfig();

      // 2. Initialize Token (Background or Blocking based on config)
      if (this.state.pluginConfig.storeId) {
        await this.initializeToken();
      } else {
        console.warn('[TagadaClient] No store ID found in plugin config. Skipping token initialization.');
        this.updateState({ isLoading: false, isInitialized: true });
      }
    } catch (error) {
      console.error('[TagadaClient] Initialization failed:', error);
      this.updateState({ isLoading: false, isInitialized: true });
    }
  }

  /**
   * Load plugin configuration
   */
  private async initializePluginConfig() {
    console.log(`[TagadaClient ${this.instanceId}] initializePluginConfig called`, {
      pluginConfigLoading: this.state.pluginConfigLoading,
      hasRawPluginConfig: !!this.config.rawPluginConfig,
    });

    if (!this.state.pluginConfigLoading) {
      console.log(`[TagadaClient ${this.instanceId}] Plugin config already loading or loaded, skipping...`);
      return;
    }

    try {
      const configVariant = this.config.localConfig || 'default';
      console.log(`[TagadaClient ${this.instanceId}] Loading plugin config with variant: ${configVariant}`);
      const config = await loadPluginConfig(configVariant, this.config.rawPluginConfig);

      console.log(`[TagadaClient ${this.instanceId}] Plugin config loaded:`, config);

      this.updateState({
        pluginConfig: config,
        pluginConfigLoading: false,
      });

      // Keep funnel client in sync with latest plugin config / environment
      if (this.funnel) {
        this.funnel.setConfig({
          pluginConfig: config,
          environment: this.state.environment,
        });
      }
    } catch (error) {
      console.error('[TagadaClient] Failed to load plugin config:', error);
      this.updateState({
        pluginConfig: { basePath: '/', config: {} },
        pluginConfigLoading: false,
      });
    }
  }

  /**
   * Initialize token and session
   */
  private async initializeToken() {
    // 🔐 PRIORITY 1: Check for authCode (cross-domain handoff)
    // This ALWAYS takes precedence over existing tokens
    if (shouldResolveAuthCode()) {
      const storeId = this.state.pluginConfig.storeId;
      if (!storeId) {
        console.error('[TagadaClient] Cannot resolve authCode: storeId not found in config');
        return this.fallbackToNormalFlow();
      }

      console.log(`[TagadaClient ${this.instanceId}] 🔐 Cross-domain auth detected, resolving...`);

      try {
        const authCode = new URLSearchParams(window.location.search).get('authCode');
        if (!authCode) {
          return this.fallbackToNormalFlow();
        }

        // Resolve the handoff
        const handoffData = await resolveAuthHandoff(
          authCode,
          storeId,
          this.state.environment.apiConfig.baseUrl,
          this.state.debugMode
        );

        console.log(`[TagadaClient ${this.instanceId}] ✅ Auth handoff resolved:`, {
          customerId: handoffData.customer.id,
          role: handoffData.customer.role,
          hasContext: Object.keys(handoffData.context).length > 0,
        });

        // Set the new token (already stored by resolveAuthHandoff)
        this.setToken(handoffData.token);

        // Decode session from token
        const decodedSession = decodeJWTClient(handoffData.token);
        if (decodedSession) {
          this.updateState({ session: decodedSession });
          await this.initializeSession(decodedSession);

          // If context has funnelSessionId, restore it
          if (handoffData.context?.funnelSessionId && this.funnel) {
            if (this.state.debugMode) {
              console.log(`[TagadaClient ${this.instanceId}] Restoring funnel session from handoff context:`, handoffData.context.funnelSessionId);
            }
            // The funnel client will pick this up during auto-initialization
          }
        } else {
          console.error('[TagadaClient] Failed to decode token from handoff');
          this.updateState({ isInitialized: true, isLoading: false });
        }

        return; // ✅ Auth handoff resolved successfully, exit early
      } catch (error) {
        console.error(`[TagadaClient ${this.instanceId}] ❌ Auth handoff failed, falling back to normal flow:`, error);
        // Fall through to normal initialization
      }
    }

    // Continue with normal flow if no authCode or resolution failed
    await this.fallbackToNormalFlow();
  }

  /**
   * Normal token initialization flow (no cross-domain handoff)
   */
  private async fallbackToNormalFlow() {
    // 🔒 CRITICAL: Read URL token FIRST before any async operations
    // This prevents race conditions where anonymous token creation overwrites URL token
    const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const queryToken = urlParams.get('token');

    // If URL has token, set it IMMEDIATELY to prevent any race condition
    if (queryToken) {
      if (this.state.debugMode) {
        console.log(`[TagadaClient ${this.instanceId}] 🔒 URL token detected, setting immediately to prevent race condition`);
      }
      // Set token on API client immediately
      this.apiClient.updateToken(queryToken);
      // Also persist to localStorage
      setClientToken(queryToken);
    }

    // Now check storage (which should have the URL token if we just set it)
    const existingToken = getClientToken();

    console.log(`[TagadaClient ${this.instanceId}] Initializing token (normal flow)...`, {
      hasExistingToken: !!existingToken,
      hasQueryToken: !!queryToken,
      storeId: this.state.pluginConfig.storeId,
    });

    let tokenToUse: string | null = null;
    let shouldPersist = false;

    const storeId = this.state.pluginConfig.storeId;
    if (queryToken) {
      tokenToUse = queryToken;
      shouldPersist = true;
    } else if (existingToken && !isTokenExpired(existingToken) && (!storeId || decodeJWTClient(existingToken)?.storeId === storeId)) {
      tokenToUse = existingToken;
    }

    if (tokenToUse) {
      this.setToken(tokenToUse);

      // Persist token if it came from query (updates localStorage and fires event)
      // We do this AFTER setToken so state is updated and handleStorageChange sees match
      if (shouldPersist) {
        if (this.state.debugMode) {
          console.log(`[TagadaClient ${this.instanceId}] Persisting query token to storage...`);
        }
        setClientToken(tokenToUse);
      }

      const decodedSession = decodeJWTClient(tokenToUse);
      if (decodedSession) {
        this.updateState({ session: decodedSession });
        await this.initializeSession(decodedSession);
      } else {
        console.error('[TagadaClient] Failed to decode token');
        this.updateState({ isInitialized: true, isLoading: false });
      }
    } else {
      // Create anonymous token
      console.log(`[TagadaClient ${this.instanceId}] No existing token, creating anonymous token...`, {
        hasStoreId: !!storeId,
        storeId,
      });
      if (storeId) {
        await this.createAnonymousToken(storeId);
      } else {
        console.warn(`[TagadaClient ${this.instanceId}] No storeId in plugin config, skipping anonymous token creation`);
        this.updateState({ isInitialized: true, isLoading: false });
      }
    }
  }

  /**
   * Set token and resolve waiting requests
   */
  private setToken(token: string | null) {
    this.apiClient.updateToken(token);
    this.updateState({ token });

    // Notify waiting requests
    if (this.tokenResolver) {
      this.tokenResolver(token);
      this.tokenPromise = null;
      this.tokenResolver = null;
    } else {
      // If we set a token but no one was waiting, ensure future requests don't wait unnecessarily
      // Actually, if we have a token, the provider won't be called by ApiClient logic if we handle it right.
      // But to be safe, if we set a token, we can pre-resolve the promise if it exists.
      // Wait, if tokenPromise exists, tokenResolver must exist.
    }
  }

  /**
   * Wait for token to be available
   */
  private waitForToken(): Promise<string | null> {
    if (this.apiClient.getCurrentToken()) {
      return Promise.resolve(this.apiClient.getCurrentToken());
    }

    if (!this.tokenPromise) {
      this.tokenPromise = new Promise<string | null>((resolve) => {
        this.tokenResolver = resolve;
      });
    }

    return this.tokenPromise;
  }

  /**
   * Create anonymous token
   */
  async createAnonymousToken(storeId: string) {
    // 🔒 CRITICAL: Never create anonymous token if URL has a token
    // This prevents race conditions during forceReset
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      if (urlToken) {
        if (this.state.debugMode) {
          console.log(`[TagadaClient ${this.instanceId}] 🔒 URL has token, skipping anonymous token creation`);
        }
        // Use the URL token instead
        this.setToken(urlToken);
        setClientToken(urlToken);
        const decodedSession = decodeJWTClient(urlToken);
        if (decodedSession) {
          this.updateState({ session: decodedSession });
          await this.initializeSession(decodedSession);
        }
        return;
      }
    }

    // Prevent concurrent anonymous token creation
    if (this.isInitializingSession) {
      if (this.state.debugMode) {
        console.log(`[TagadaClient ${this.instanceId}] Session initialization in progress, skipping anonymous token creation`);
      }
      return;
    }

    try {
      await this.mintAnonymousToken(storeId);

      this.updateState({ isSessionInitialized: true });

    } catch (error) {
      console.error('[TagadaClient] Failed to create anonymous token:', error);
      this.updateState({ isInitialized: true, isLoading: false });
    }
  }

  /**
   * Mint an anonymous token for the store, keep it, and start its session
   */
  private async mintAnonymousToken(storeId: string): Promise<string> {
    // 🎯 Check draft mode from URL, localStorage, or cookie
    const draft = isDraftMode();

    if (this.state.debugMode) {
      console.log('[TagadaClient] Creating anonymous token for store:', storeId, { draft });
    }

    // We use fetch directly or ApiClient with skipAuth to avoid waiting for itself
    const response = await this.apiClient.post<AnonymousTokenResponse>(
      '/api/v1/cms/session/anonymous',
      {
        storeId,
        role: 'anonymous',
        draft, // 🎯 Pass draft mode to anonymous login
      },
      { skipAuth: true }
    );

    this.setToken(response.token);
    setClientToken(response.token);

    const decodedSession = decodeJWTClient(response.token);
    if (decodedSession) {
      this.updateState({ session: decodedSession });
      await this.initializeSession(decodedSession);
    }
    return response.token;
  }

  /**
   * A new anonymous token for the configured store when the server refused the current one: once per page, shared by
   * every call the same refusal hit. A token already replaced answers its replacement; a second refusal answers null.
   */
  private renewRejectedToken(rejectedToken: string): Promise<string | null> {
    if (this.tokenRenewal && rejectedToken === this.renewedToken) return this.tokenRenewal;
    if (this.state.token !== rejectedToken) return Promise.resolve(this.state.token);
    const storeId = this.state.pluginConfig.storeId;
    if (this.renewedToken !== null || !storeId) return Promise.resolve(null);

    console.warn(`[TagadaClient ${this.instanceId}] The server refused the session token, creating a new one`);
    this.renewedToken = rejectedToken;
    // The funnel session belongs to the refused session's customer
    this.funnel?.resetInitialization();
    this.tokenRenewal = this.mintAnonymousToken(storeId).catch((error) => {
      console.error('[TagadaClient] Failed to renew the session token:', error);
      return null;
    });
    return this.tokenRenewal;
  }

  /**
   * Initialize session
   */
  async initializeSession(sessionData: Session) {
    // Prevent concurrent initialization attempts
    if (this.isInitializingSession) {
      if (this.state.debugMode) {
        console.log(`[TagadaClient ${this.instanceId}] Session initialization already in progress, skipping`);
      }
      return;
    }

    this.isInitializingSession = true;
    const sentToken = this.apiClient.getCurrentToken();
    let rejectedToken: string | null = null;

    try {
      if (this.state.debugMode) {
        console.log(`[TagadaClient ${this.instanceId}] Initializing session...`, { sessionId: sessionData.sessionId });
      }

      const deviceInfo = await collectDeviceInfo();
      const urlParams = getUrlParams();
      const browserLocale = getBrowserLocale();

      // 🎯 Check draft mode from URL, localStorage, or cookie
      const draft = isDraftMode();

      // Store draft mode if detected from URL (urlParams may have draft from query string)
      const draftParam = new URLSearchParams(window.location.search).get('draft');
      if (draftParam !== null) {
        setDraftMode(draftParam === 'true');
      }

      const sessionInitData = {
        storeId: sessionData.storeId,
        accountId: sessionData.accountId,
        customerId: sessionData.customerId,
        role: sessionData.role,
        browserLocale,
        queryLocale: urlParams.locale,
        queryCurrency: urlParams.currency,
        utmSource: urlParams.utmSource,
        utmMedium: urlParams.utmMedium,
        utmCampaign: urlParams.utmCampaign,
        gclid: urlParams.gclid,
        browser: deviceInfo.userAgent.browser.name,
        browserVersion: deviceInfo.userAgent.browser.version,
        os: deviceInfo.userAgent.os.name,
        osVersion: deviceInfo.userAgent.os.version,
        deviceType: deviceInfo.userAgent.device?.type,
        deviceModel: deviceInfo.userAgent.device?.model,
        deviceVendor: deviceInfo.userAgent.device?.vendor,
        userAgent: deviceInfo.userAgent.name,
        engineName: deviceInfo.userAgent.engine.name,
        engineVersion: deviceInfo.userAgent.engine.version,
        cpuArchitecture: deviceInfo.userAgent.cpu.architecture,
        isBot: deviceInfo.flags?.isBot ?? false,
        isChromeFamily: deviceInfo.flags?.isChromeFamily ?? false,
        isStandalonePWA: deviceInfo.flags?.isStandalonePWA ?? false,
        isAppleSilicon: deviceInfo.flags?.isAppleSilicon ?? false,
        screenWidth: deviceInfo.screenResolution.width,
        screenHeight: deviceInfo.screenResolution.height,
        timeZone: deviceInfo.timeZone,
        draft, // 🎯 Pass draft mode to session init
        fetchMessages: false,
      };

      // A refused token is renewed below, with a session of its own: a retry would resend this one's ids
      const response = await this.apiClient.post<SessionInitResponse>(
        '/api/v1/cms/session/v2/init',
        sessionInitData,
        { renewRejectedToken: false }
      );

      // Success - reset error tracking
      this.lastSessionInitError = null;
      this.sessionInitRetryCount = 0;

      // Update state with session data
      this.updateSessionState(response, sessionData);

      this.updateState({
        isInitialized: true,
        isSessionInitialized: true,
        isLoading: false,
      });

      if (this.state.debugMode) console.log('[TagadaClient] Session initialized successfully');

    } catch (error) {
      // Track error and increment retry count
      this.lastSessionInitError = error as Error;
      this.sessionInitRetryCount++;

      console.error(`[TagadaClient] Error initializing session (attempt ${this.sessionInitRetryCount}/${this.MAX_SESSION_INIT_RETRIES}):`, error);

      rejectedToken = isRejectedSession(error) ? sentToken : null;
      if (!rejectedToken) {
        this.updateState({
          isInitialized: true,
          isLoading: false,
        });
      }
    } finally {
      // Always release the lock
      this.isInitializingSession = false;
    }

    // Outside the lock: the renewal initializes the new token's session itself
    if (rejectedToken && !(await this.renewRejectedToken(rejectedToken))) {
      this.updateState({ isInitialized: true, isLoading: false });
    }
  }

  private updateSessionState(response: SessionInitResponse, sessionData: Session) {
    // Update Store
    if (response.store) {
      const storeData = response.store as any;
      const storeConfig = {
        ...response.store,
        // Ensure accountId is included - fallback to plugin config or session accountId
        accountId: storeData.accountId || this.state.pluginConfig?.accountId || sessionData.accountId || '',
        presentmentCurrencies: storeData.presentmentCurrencies || [response.store.currency || 'USD'],
        chargeCurrencies: storeData.chargeCurrencies || [response.store.currency || 'USD'],
      };
      this.updateState({ store: storeConfig });
    }

    // Update Locale (only if provided - V2 endpoint doesn't return locale)
    if (response.locale) {
      const localeConfig = {
        locale: response.locale,
        language: response.locale.split('-')[0],
        region: response.locale.split('-')[1] ?? 'US',
        messages: response.messages ?? {},
      };
      this.updateState({ locale: localeConfig });
    }

    // Update Currency
    if (response.store) {
      const currencyConfig = {
        code: response.store.currency,
        symbol: this.getCurrencySymbol(response.store.currency),
        name: this.getCurrencyName(response.store.currency),
      };
      this.updateState({ currency: currencyConfig });
    }

    // Update Customer & Auth
    const authState = {
      isAuthenticated: response.customer?.isAuthenticated ?? false,
      isLoading: false,
      customer: response.customer ?? null,
      session: sessionData,
    };
    this.updateState({
      customer: response.customer ?? null,
      auth: authState
    });

    // Auto-initialize funnel if enabled (skip when caller handles init explicitly)
    const funnelFeature = this.config.features?.funnel;
    const skipAutoInit = typeof funnelFeature === 'object' && funnelFeature.skipAutoInit;
    if (this.funnel && !skipAutoInit && sessionData.customerId && response.store?.id) {
      const accountId = response.store.accountId || this.state.pluginConfig?.accountId || sessionData.accountId || '';

      if (accountId) {
        // Get funnelId from URL or config
        const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
        const funnelId = urlParams.get('funnelId') || undefined;

        if (this.state.debugMode) {
          console.log('[TagadaClient] Auto-initializing funnel...', {
            customerId: sessionData.customerId,
            storeId: response.store.id,
            accountId,
            funnelId: funnelId || 'auto-detect',
          });
        }

        // Auto-initialize funnel in background (don't block session init)
        this.funnel.autoInitialize(
          { customerId: sessionData.customerId, sessionId: sessionData.sessionId },
          { id: response.store.id, accountId },
          funnelId,
        ).catch((err) => {
          console.error('[TagadaClient] Funnel auto-initialization failed:', err);
        });
      } else {
        console.warn('[TagadaClient] Cannot auto-initialize funnel: accountId is missing');
      }
    }
  }

  // Helper methods
  private getCurrencySymbol(code: string): string {
    const symbols: Record<string, string> = {
      USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$',
    };
    return symbols[code] || code;
  }

  private getCurrencyName(code: string): string {
    const names: Record<string, string> = {
      USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound',
      JPY: 'Japanese Yen', CAD: 'Canadian Dollar', AUD: 'Australian Dollar',
    };
    return names[code] || code;
  }

  /**
   * Helper to get accountId with fallbacks
   */
  getAccountId(): string {
    return this.state.store?.accountId || this.state.pluginConfig?.accountId || this.state.session?.accountId || '';
  }

  /**
   * Update plugin config dynamically (hot-reload)
   * Used for live config editing without page reload
   */
  updatePluginConfig(newConfig: Partial<PluginConfig['config']>) {
    if (this.state.debugMode) {
      console.log('[TagadaClient] Hot-reloading config:', newConfig);
    }

    // Merge new config with existing
    const updatedConfig = {
      ...this.state.pluginConfig.config,
      ...newConfig,
    };

    // Update state
    this.updateState({
      pluginConfig: {
        ...this.state.pluginConfig,
        config: updatedConfig,
      },
    });

    // Emit config update event
    this.bus.emit('CONFIG_UPDATED', updatedConfig);

    if (this.state.debugMode) {
      console.log('[TagadaClient] Config updated successfully');
    }
  }

  /**
   * Setup listener for config updates from parent window (config editor)
   * Enables live config editing via postMessage
   */
  private setupConfigHotReload() {
    if (typeof window === 'undefined') return;

    const handleMessage = (event: MessageEvent) => {
      // Security: Only accept messages from same origin or trusted origins
      // In production, you might want to check event.origin more strictly

      if (event.data?.type === 'TAGADAPAY_CONFIG_UPDATE') {
        const { config } = event.data;

        if (this.state.debugMode) {
          console.log('[TagadaClient] Received config update from parent:', config);
        }

        this.updatePluginConfig(config);
      } else if (event.data?.type === 'APPLY_STYLES_TO_ELEMENT') {
        const { elementId, styles } = event.data;

        if (this.state.debugMode) {
          console.log('[TagadaClient] Received style application request:', { elementId, styles });
        }

        this.applyStylesToElement(elementId, styles);
      }
    };

    window.addEventListener('message', handleMessage);

    if (this.state.debugMode) {
      console.log('[TagadaClient] Config hot-reload and style manipulation listeners enabled');
    }
  }

  /**
   * Apply CSS properties to a real div child element for highlighting
   * Used by config editor to highlight elements in preview without altering the element's style
   */
  private applyStylesToElement(elementId: string, styles?: Record<string, string>) {
    if (typeof document === 'undefined') return;

    // Always remove any existing highlight, even if the target element is not found
    const staticHighlightId = 'tagada-editor-highlight';
    const existingHighlight = document.getElementById(staticHighlightId) as HTMLElement | null;
    if (existingHighlight) {
      existingHighlight.remove();
    }

    const applyToElement = (element: HTMLElement) => {
      // List of void/self-closing elements that don't accept children
      const voidElements = new Set([
        'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
        'link', 'meta', 'param', 'source', 'track', 'wbr'
      ]);

      const isVoidElement = voidElements.has(element.tagName.toLowerCase());

      // For void elements, wrap them in a container div
      let targetElement = element;

      if (isVoidElement) {
        // Check if element is already wrapped (from a previous highlight)
        const parent = element.parentElement;
        const existingWrapper = parent?.getAttribute('data-tagada-highlight-wrapper');

        if (existingWrapper === 'true' && parent instanceof HTMLElement) {
          // Reuse existing wrapper
          targetElement = parent;
        } else {
          // Create a new wrapper
          const newWrapper = document.createElement('div');
          newWrapper.setAttribute('data-tagada-highlight-wrapper', 'true');

          // Preserve the element's layout behavior by matching its display style
          // This minimizes the visual impact of the wrapper
          const computedStyle = window.getComputedStyle(element);
          const elementDisplay = computedStyle.display;

          // Match the display type to preserve layout
          // For inline elements, use inline-block (to support position: relative)
          // For all others, use the same display value
          if (elementDisplay === 'inline') {
            newWrapper.style.display = 'inline-block';
          } else {
            newWrapper.style.display = elementDisplay;
          }

          newWrapper.style.position = 'relative';

          // Preserve vertical alignment for inline elements
          if (elementDisplay === 'inline' || elementDisplay.includes('inline')) {
            const verticalAlign = computedStyle.verticalAlign;
            if (verticalAlign && verticalAlign !== 'baseline') {
              newWrapper.style.verticalAlign = verticalAlign;
            }
          }

          // Preserve spacing and layout properties from the original element
          // List of CSS properties that affect spacing and layout
          const spacingProperties = [
            // Width and height
            'width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight',
            // Flex properties
            'flex', 'flexGrow', 'flexShrink', 'flexBasis',
            // Grid properties
            'gridColumn', 'gridRow', 'gridColumnStart', 'gridColumnEnd',
            'gridRowStart', 'gridRowEnd', 'gridArea',
            // Alignment properties
            'alignSelf', 'justifySelf',
            // Box sizing
            'boxSizing',
            // Gap (for grid/flex)
            'gap', 'rowGap', 'columnGap',
            // Order (for flex/grid)
            'order',
            // Aspect ratio
            'aspectRatio',
          ];

          spacingProperties.forEach(prop => {
            const camelProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
            const value = computedStyle.getPropertyValue(camelProp);

            // Only copy if the value is not empty
            // For most properties, we want to preserve the value even if it's 'auto' or '0px'
            // as these might be intentional layout choices
            if (value && value.trim() !== '') {
              // Handle special cases
              if (prop === 'flex' && value !== 'none' && value !== '0 1 auto') {
                newWrapper.style.flex = value;
              } else if (prop === 'boxSizing') {
                newWrapper.style.boxSizing = value;
              } else {
                // Use setProperty for kebab-case properties
                // Preserve all values including percentages, auto, etc.
                newWrapper.style.setProperty(camelProp, value);
              }
            }
          });

          // Also check inline styles for spacing properties
          // This handles cases where styles are set directly on the element
          if (element.style) {
            const inlineSpacingProps = [
              'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
              'flex', 'flex-grow', 'flex-shrink', 'flex-basis',
              'grid-column', 'grid-row', 'grid-column-start', 'grid-column-end',
              'grid-row-start', 'grid-row-end', 'grid-area',
              'align-self', 'justify-self',
              'box-sizing', 'gap', 'row-gap', 'column-gap', 'order', 'aspect-ratio'
            ];

            inlineSpacingProps.forEach(prop => {
              const inlineValue = element.style.getPropertyValue(prop);
              if (inlineValue) {
                newWrapper.style.setProperty(prop, inlineValue);
              }
            });
          }

          // Insert wrapper before element
          element.parentNode?.insertBefore(newWrapper, element);
          // Move element into wrapper
          newWrapper.appendChild(element);
          targetElement = newWrapper;
        }
      }

      // Ensure element has position relative for absolute child positioning
      const computedStyle = getComputedStyle(isVoidElement ? element : targetElement);
      if (targetElement.style.position === 'static' || !targetElement.style.position) {
        targetElement.style.position = 'relative';
      }

      // Create new highlight div with static ID
      const highlightDiv = document.createElement('div');
      highlightDiv.id = staticHighlightId;
      highlightDiv.style.position = 'absolute';
      highlightDiv.style.inset = '0';
      highlightDiv.style.pointerEvents = 'none';
      highlightDiv.style.zIndex = '9999';
      highlightDiv.style.boxSizing = 'border-box';
      highlightDiv.style.background = 'none';
      highlightDiv.style.border = 'none';
      highlightDiv.style.outline = 'none';
      highlightDiv.style.margin = '0';
      highlightDiv.style.padding = '0';

      // Inherit border radius from parent
      const borderRadius = computedStyle.borderRadius;
      if (borderRadius) {
        highlightDiv.style.borderRadius = borderRadius;
      }

      // Append the new highlight div to the target element
      targetElement.appendChild(highlightDiv);

      // Apply CSS properties to the highlight div
      const stylesToApply = styles || { boxShadow: '0 0 0 2px rgb(239 68 68)' }; // Default red ring

      Object.entries(stylesToApply).forEach(([property, value]) => {
        // Convert kebab-case to camelCase for style object
        const camelProperty = property.includes('-')
          ? property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
          : property;

        // Use setProperty for kebab-case or direct assignment for camelCase
        if (property.includes('-')) {
          highlightDiv.style.setProperty(property, value);
        } else {
          (highlightDiv.style as any)[camelProperty] = value;
        }
      });

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      if (this.state.debugMode) {
        const appliedStyles = Object.entries(stylesToApply).map(([k, v]) => `${k}: ${v}`).join('; ');
        console.log(`[TagadaClient] Applied styles to highlight div of element #${elementId}:`, appliedStyles);
      }
    };

    // Support multiple IDs in editor-id attribute (space-separated)
    // Use ~= selector to match elementId as a space-separated value
    const maxAttempts = 5;
    const intervalMs = 1000;
    let attempts = 0;

    const isElementHidden = (element: HTMLElement): boolean => {
      const style = window.getComputedStyle(element);
      return (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0' ||
        element.hidden ||
        element.offsetWidth === 0 ||
        element.offsetHeight === 0
      );
    };

    const findAndApply = () => {
      const elements = document.querySelectorAll(`[editor-id~="${elementId}"]`) as NodeListOf<HTMLElement>;

      if (elements.length === 0) {
        attempts += 1;

        if (attempts >= maxAttempts) {
          if (this.state.debugMode) {
            console.warn(
              `[TagadaClient] Element with editor-id containing "${elementId}" not found after ${maxAttempts} attempts`
            );
          }
          return;
        }

        if (this.state.debugMode) {
          console.warn(
            `[TagadaClient] Element with editor-id containing "${elementId}" not found (attempt ${attempts}/${maxAttempts}), retrying in ${intervalMs / 1000}s`
          );
        }

        setTimeout(findAndApply, intervalMs);
        return;
      }

      // If multiple elements found, prioritize the one that is not hidden
      let element: HTMLElement | null = null;

      if (elements.length === 1) {
        element = elements[0];
      } else {
        // Find the first non-hidden element
        for (let i = 0; i < elements.length; i++) {
          if (!isElementHidden(elements[i])) {
            element = elements[i];
            break;
          }
        }
        // If all are hidden, use the first one
        if (!element) {
          element = elements[0];
        }
      }

      if (element) {
        applyToElement(element);
      }
    };

    findAndApply();
  }
}

